import {
  BrDateTimePicker,
  BrDivider,
  BrInput,
  BrLoading,
  BrSelect,
  BrTextarea,
  Col,
  Row,
} from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { useContext, useEffect, useState } from 'react';
import InfoCircleTooltip from '../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import { IAquisicao } from '../../../../../../../interfaces/IAquisicao';
import { IMunicipio, IProcessoMinerario } from '../../../../../../../interfaces/IProcessos';
import { formatarNrProcesso } from '../../../../../../../utils/formatNrProcesso';
import { onTextAreaChange } from '../../../../../../../utils/textAreaSize';
import { tratarMsgErroRequisicoesBack } from '../../../../../../../utils/tratarMsgErro';
import { useBackEndRequests } from '../../../../../lib/backEndRequests';
import styles from './primeiroAdquirenteAquisicaoForm.module.scss';
import { DiefCfemContext } from '../../../../../../../contexts/DiefCfemContext';
import { formatarCNPJOrCpf } from '../../../../../../../utils/formatCNPJOrCPF';

interface IPrimeiroAdquirenteProcessosFormProps {
  readonly numeroProcesso: string;
  readonly setNumeroProcesso: React.Dispatch<React.SetStateAction<string>>;
  readonly substancia: string;
  readonly setSubstancia: React.Dispatch<React.SetStateAction<string>>;
  readonly municipio: IMunicipio;
  readonly setMunicipio: React.Dispatch<React.SetStateAction<IMunicipio | undefined>>;
  readonly unidadeMedida: string;
  readonly setUnidadeMedida: React.Dispatch<React.SetStateAction<string>>;
  readonly estabelecimentoAdquirente: string;
  readonly setEstabelecimentoAdquirente: React.Dispatch<React.SetStateAction<string>>;
  readonly isRejeito: boolean;
  readonly setIsRejeito: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setIsWithError: React.Dispatch<React.SetStateAction<boolean>>;
  readonly hasIrregularidade: boolean;
  readonly setHasIrregularidade: React.Dispatch<React.SetStateAction<boolean>>;
  readonly irregularidade: string;
  readonly setIrregularidade: React.Dispatch<React.SetStateAction<string>>;
  readonly edicaoAquisicao: IAquisicao | undefined;
  readonly isDadosMinerariosSalvos: boolean;
  readonly observacoes: string;
  readonly setObservacoes: React.Dispatch<React.SetStateAction<string>>;
}

function PrimeiroAdquirenteProcessosForm({
  numeroProcesso,
  setNumeroProcesso,
  substancia,
  setSubstancia,
  municipio,
  setMunicipio,
  unidadeMedida,
  setUnidadeMedida,
  estabelecimentoAdquirente,
  setEstabelecimentoAdquirente,
  isRejeito,
  setIsRejeito,
  setIsWithError,
  hasIrregularidade,
  setHasIrregularidade,
  irregularidade,
  setIrregularidade,
  edicaoAquisicao,
  isDadosMinerariosSalvos,
  observacoes,
  setObservacoes,
}: IPrimeiroAdquirenteProcessosFormProps) {
  const [numeroProcessoFeedbackText, setNumeroProcessoFeedbackText] = useState('');
  const [numeroProcessoStatus, setNumeroProcessoStatus] = useState<
    'success' | 'danger' | 'info' | 'warning' | undefined
  >(undefined);

  const [isProcessoPreenchido, setIsProcessoPreenchido] = useState(false);
  const [isSubstanciaPreenchida, setIsSubstanciaPreenchida] = useState(false);
  const [municipioLabel, setMunicipioLabel] = useState('');

  const { getIsProcessosPLG } = useBackEndRequests();

  const [municipios, setMunicipios] = useState<IMunicipio[]>([]);
  const [substancias, setSubstancias] = useState([
    {
      label: '',
      value: '',
    },
  ]);
  const [unidadesMedida, setUnidadesMedida] = useState([
    {
      label: '',
      value: '',
    },
  ]);

  const context = useContext(DiefCfemContext);

  let estabelecimentoAdquirenteOptions: { label: string; value: string }[] = [
    {
      label:
        formatarCNPJOrCpf(context?.declaracaoPrimeiroAdquirente?.declarante?.cpfCnpj || '') +
        ' - ' +
        context?.declaracaoPrimeiroAdquirente?.declarante?.nomeRazaoSocial,
      value: context?.declaracaoPrimeiroAdquirente?.declarante?.cpfCnpj || '',
    },
  ];

  estabelecimentoAdquirenteOptions = [
    ...estabelecimentoAdquirenteOptions,
    ...((context?.declaracaoPrimeiroAdquirente?.filiais || []).map((filial) => ({
      label: formatarCNPJOrCpf(filial?.cnpj || '') + ' - ' + filial?.nomeRazaoSocial,
      value: filial?.cnpj || '',
    })) || []),
  ];

  const {
    data: processoData,
    isLoading: isProcessoDataLoading,
    error: processoDataError,
  } = useQuery<IProcessoMinerario, IErrorWithSentryId>({
    queryKey: [
      'processoPrimeiroAdquirenteQuery',
      [numeroProcesso, sessionStorage.getItem('mesDeclaracao'), sessionStorage.getItem('anoDeclaracao')],
    ],
    queryFn: () => getIsProcessosPLG(numeroProcesso.replace(/[./]/g, '')),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: numeroProcesso.length === 12,
    staleTime: 0,
  });

  const onSubstanciaChange = (value: string, unidadeMedida?: string) => {
    setSubstancia(value);

    const substancia = processoData?.substancias.find((item) => item.nome === value);

    const unidadesMedida = substancia
      ? substancia.unidadesMedida.map((unidade) => ({
          label: unidade, // Unidade como label
          value: unidade, // Unidade como value (pode ajustar se necessário)
        }))
      : [];

    setUnidadesMedida(unidadesMedida);

    unidadeMedida && edicaoAquisicao !== undefined
      ? setUnidadeMedida(unidadeMedida)
      : setUnidadeMedida(unidadesMedida[0]?.value);

    setIsSubstanciaPreenchida(true);
  };

  const onIrregularidadeChange = function (value: string) {
    onTextAreaChange(value, irregularidade, setIrregularidade);
  };

  const onObservacaoChange = function (value: string) {
    onTextAreaChange(value, observacoes, setObservacoes);
  };

  useEffect(() => {
    if (processoData) {
      if (processoData.isLavraGarimpeira) {
        setIsProcessoPreenchido(true);
        setNumeroProcessoFeedbackText('');
        setNumeroProcessoStatus(undefined);
        setIsWithError(false);

        const municipios: IMunicipio[] = processoData.municipios;

        setMunicipios(municipios);

        let municipioSelecionado = null;

        if (municipio?.codigoIBGE !== '' && edicaoAquisicao !== undefined) {
          municipioSelecionado = municipios.find((m) => m.codigoIBGE.toString() === municipio.codigoIBGE.toString());
        }

        municipioSelecionado ??= municipios[0];

        setMunicipio(municipioSelecionado);
        setMunicipioLabel(`${municipioSelecionado.nome}/${municipioSelecionado.uf}`);

        const substancias = processoData.substancias.map((substancias) => ({
          label: substancias.nome, // Nome do município para o campo 'label'
          value: substancias.nome, // Código IBGE para o campo 'value'
        }));

        setSubstancias(substancias);

        if (edicaoAquisicao !== undefined) {
          onSubstanciaChange(substancia, unidadeMedida);
        } else {
          onSubstanciaChange(substancias[0].value);
        }
      } else {
        setIsProcessoPreenchido(false);
        setIsSubstanciaPreenchida(false);
        setNumeroProcessoFeedbackText('Processo não possui PLG outorgada');
        setNumeroProcessoStatus('danger');
        setIsWithError(true);
      }
    } else if ((processoData !== undefined && !processoData) || processoDataError?.status === 404) {
      setIsProcessoPreenchido(false);
      setIsSubstanciaPreenchida(false);
      setNumeroProcessoFeedbackText('Processo não encontrado');
      setNumeroProcessoStatus('danger');
      setIsWithError(true);
    } else if ((processoData !== undefined && !processoData) || processoDataError?.status === 400) {
      setNumeroProcessoFeedbackText(tratarMsgErroRequisicoesBack(processoDataError));
      setIsProcessoPreenchido(false);
      setIsSubstanciaPreenchida(false);
      setNumeroProcessoStatus('danger');
      setIsWithError(true);
    }
  }, [processoData, processoDataError]);

  const formatarMunicipio = function () {
    if (municipio) {
      return `${municipio.nome}/${municipio.uf}`;
    } else {
      return '';
    }
  };

  const changeNumeroProcesso = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumeroProcesso(event.target.value);
    if (event.target.value.length === 0) {
      setNumeroProcessoFeedbackText('Campo obrigatório');
      setNumeroProcessoStatus('danger');
      setIsWithError(true);
    } else {
      setNumeroProcessoFeedbackText('');
      setNumeroProcessoStatus(undefined);
      setIsWithError(false);
    }
  };

  return (
    <div className={styles.container}>
      {isProcessoDataLoading && (
        <div className={styles.loadContainer}>
          <BrLoading large label='Carregando...' />
        </div>
      )}

      <Row className={styles.row}>
        <Col className={styles.dadosCol}>
          {!isDadosMinerariosSalvos ? (
            <BrInput
              label='Nº do Processo Minerário'
              autoComplete='off'
              mask='###.###/####'
              value={numeroProcesso}
              onChange={(e) => changeNumeroProcesso(e)}
              feedbackText={numeroProcessoFeedbackText}
              status={numeroProcessoStatus}
              disabled={edicaoAquisicao !== undefined}
            />
          ) : (
            <label className={styles.label}>
              Nº do Processo Minerário{' '}
              <span className={styles.dadoAdicionado}>{formatarNrProcesso(numeroProcesso)}</span>
            </label>
          )}
        </Col>
        <Col className={styles.dadosCol}>
          {!isDadosMinerariosSalvos ? (
            <BrSelect
              label='Município/UF'
              className={styles.select}
              options={municipios.map((municipio) => ({
                label: `${municipio.nome}/${municipio.uf}`,
                value: municipio.codigoIBGE,
              }))} // Ajustar conforme necessário
              disabled={!isProcessoPreenchido}
              value={municipio?.codigoIBGE}
              placeholder={municipioLabel}
              onChange={(value: string) => {
                const selectedMunicipio = municipios.find((m) => m.codigoIBGE.toString() === value);
                setMunicipio(selectedMunicipio);
                setMunicipioLabel(selectedMunicipio ? `${selectedMunicipio.nome}/${selectedMunicipio.uf}` : '');
              }}
            />
          ) : (
            <label className={styles.label}>
              Município/UF <span className={styles.dadoAdicionado}>{formatarMunicipio()}</span>
            </label>
          )}
        </Col>
        <Col className={styles.dadosCol}>
          {!isDadosMinerariosSalvos ? (
            <BrSelect
              label='Substância'
              className={styles.select}
              options={substancias}
              disabled={!isProcessoPreenchido}
              value={substancia}
              placeholder={substancia}
              onChange={(value: string) => onSubstanciaChange(value)}
            />
          ) : (
            <label className={styles.label}>
              Substância <span className={styles.dadoAdicionado}>{substancia}</span>
            </label>
          )}
        </Col>
      </Row>
      <Row className={styles.row}>
        <Col className={styles.dadosCol}>
          {!isDadosMinerariosSalvos ? (
            <BrSelect
              label='Uni. de Medida'
              className={styles.select}
              options={unidadesMedida}
              disabled={!isSubstanciaPreenchida}
              value={unidadeMedida}
              placeholder={unidadeMedida}
              onChange={(value: string) => setUnidadeMedida(value)}
            />
          ) : (
            <span className={styles.label}>
              Uni. de Medida <span className={styles.dadoAdicionado}>{unidadeMedida}</span>
            </span>
          )}
        </Col>
        <Col></Col>
        <Col></Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row>
        <Col className={styles.dadosCol}>
          {!isDadosMinerariosSalvos ? (
            <BrSelect
              key={estabelecimentoAdquirente}
              label='Estabelecimento Adquirente'
              className={styles.select}
              options={estabelecimentoAdquirenteOptions}
              value={estabelecimentoAdquirente}
              placeholder={estabelecimentoAdquirente}
              onChange={(value: string) => setEstabelecimentoAdquirente(value)}
            />
          ) : (
            <span className={styles.label}>
              Estabelecimento Adquirente{' '}
              <span className={styles.dadoAdicionado}>
                {estabelecimentoAdquirente ? formatarCNPJOrCpf(estabelecimentoAdquirente) : '-'}
              </span>
            </span>
          )}
        </Col>
        <Col></Col>
        <Col></Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row>
        <Col>
          <label id='label-rejeitos' className={styles.tooltipLabel}>
            <InfoCircleTooltip
              text='Informação vinculada à redução da alíquota da CFEM em 50%, conforme § 7º, do Art. 6º, da Lei nº 7.990/1989. Os procedimentos para aproveitamento de rejeitos e estéreis estão dispostos na RESOLUÇÃO ANM Nº 85, DE 2 DE DEZEMBRO DE 2021.'
              id='rejeitos'
            />
            O bem mineral é oriundo de rejeitos e estéreis de minerais associados utilizados em outras cadeias
            produtivas?
          </label>
        </Col>
        <Col className={styles.checkbox}>
          <div
            className={`br-switch icon`}
            onClick={() => setIsRejeito(!isRejeito)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setIsRejeito(!isRejeito);
            }}
          >
            <input
              id='switch-icon'
              type='checkbox'
              name='switch-icon'
              checked={isRejeito}
              role='switch'
              aria-labelledby='label-rejeitos'
            />
            <label className={styles.labelSwitch}>{isRejeito ? 'Sim' : 'Não'}</label>
          </div>
        </Col>
        <Col></Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row className={styles.rowIrregularidade}>
        <Col className={styles.col}>
          <label id='label-indicios-irregularidades' className={styles.tooltipLabel}>
            <InfoCircleTooltip
              text='As pessoas físicas e jurídicas que tenham, em caráter permanente ou eventual, como atividade principal ou acessória, cumulativamente ou não, a compra e venda de moeda estrangeira ou ouro como ativo financeiro ou instrumento cambial, deverão comunicar as autoridades competentes, suas operações financeiras, dispensando especial atenção às operações que, nos termos de instruções emanadas das autoridades competentes, possam constituir-se em sérios indícios dos crimes previstos na Lei nº 9.613, de 03 de março de 1998.'
              id='irregularidades'
            />
            Há indícios de irregularidades na substância adquirida?
          </label>
        </Col>
        <Col className={styles.checkbox}>
          <div
            className={`br-switch icon`}
            onClick={() => {
              setHasIrregularidade(!hasIrregularidade);
              setIrregularidade('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setHasIrregularidade(!hasIrregularidade);
                setIrregularidade('');
              }
            }}
          >
            <input
              id='switch-icon'
              type='checkbox'
              name='switch-icon'
              checked={hasIrregularidade}
              role='switch'
              aria-labelledby='label-indicios-irregularidades'
            />
            <label className={styles.labelSwitch}>{hasIrregularidade ? 'Sim' : 'Não'}</label>
          </div>
        </Col>
        <Col className={styles.dadosCol}>
          {hasIrregularidade && (
            <BrTextarea
              label='Descreva a irregularidade encontrada'
              value={irregularidade}
              density='large'
              onChange={(e) => onIrregularidadeChange(e.target.value)}
            />
          )}
        </Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row>
        <Col>
          <label id='label-observacoes' className={styles.tooltipLabel}>
            <InfoCircleTooltip
              text='Outras observações pertinentes a quaisquer aquisições que envolvem a tríade processo minerário, substância e município preenchida acima.'
              id='observacoes'
            />
            Observações
          </label>
        </Col>
        <Col>
          <div className={styles.observacoesContainer}>
            <BrTextarea
              density='large'
              value={observacoes}
              onChange={(e) => onObservacaoChange(e.target.value)}
              aria-labelledby='label-observacoes'
            />
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default PrimeiroAdquirenteProcessosForm;
